import { useMemo } from 'react';
import { useRegistry } from '../shared.js';
/** Подписи групп полей (порядок — как в редакторе шаблона, спека §1.3). */
export const GROUP_LABEL_RU = {
    campaign: 'Кампания',
    adset: 'Группа объявлений',
    targeting: 'Таргетинг',
    creative: 'Креатив',
    schedule: 'Старт',
    copies: 'Копии и правила',
    naming: 'Названия',
};
export const GROUP_ORDER = [
    'campaign',
    'adset',
    'targeting',
    'creative',
    'schedule',
    'copies',
    'naming',
];
/**
 * Поля, у которых в пачке СВОЁ место в контракте (`AdsBatchInput.copies`,
 * `layout`, `start`, `activation`), а не `overrides`: на экране запуска они —
 * отдельные блоки, а не поля реестра.
 */
export const BATCH_LEVEL_KEYS = new Set([
    'schedule.start',
    'opt.copies',
    'opt.layout',
    'opt.activation',
]);
export function sortDescs(descs) {
    return [...descs].sort((a, b) => {
        const g = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
        return g !== 0 ? g : a.order - b.order;
    });
}
/** Все поля и опции реестра, включённые (`enabled`). */
export function allDescs(registry) {
    return sortDescs([...registry.fields, ...registry.options].filter((d) => d.enabled));
}
/** Редактируемое в шаблоне: поля и опции с `mode: template`. */
export function templateDescs(registry) {
    return allDescs(registry).filter((d) => d.mode === 'template');
}
/** «Зафиксировано системой»: `mode: fixed`. */
export function fixedDescs(registry) {
    return allDescs(registry).filter((d) => d.mode === 'fixed');
}
function isEmptyValue(v) {
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}
/**
 * Поле шаблона, которое шаблон оставил пустым, а реестр требует при запуске
 * (`requiredAtLaunchWhenEmpty`: ставка, минимальный ROAS): оно становится
 * полем запуска и обязательно, если видно (v3 §9).
 */
export function isRequiredAtLaunch(d, template) {
    return (d.mode === 'template' &&
        d.requiredAtLaunchWhenEmpty &&
        isEmptyValue(template.values[d.key]) &&
        // поле, которое задаёт КАЖДАЯ группа шаблона, — не поле запуска (v4 §3.8 B1,
        // auto-ads `isLaunchFieldOf` + `groupOwnedFields`; ревью SB-7 P0-1)
        !groupOwnedKeys(template).has(d.key));
}
/**
 * Поля, которые задаёт каждая группа шаблона (`fieldsSetByAllGroups` auto-ads:
 * значение есть и не `null`); без групп — пусто. Все группы шаблона — как
 * у сервера в `overrideIssues` (там выбор запуска не учитывается).
 */
export function groupOwnedKeys(template) {
    const groups = template.adSetGroups?.groups ?? [];
    if (groups.length === 0)
        return new Set();
    const isSet = (v, k) => Object.hasOwn(v, k) && v[k] !== null && v[k] !== undefined;
    const [first, ...rest] = groups;
    return new Set(Object.keys(first.values).filter((k) => isSet(first.values, k) && rest.every((g) => isSet(g.values, k))));
}
/**
 * Поля блока «Бюджет и ставка» экрана запуска: `mode: launch`, поля
 * шаблона из `launchEditable` и пустые в шаблоне поля «обязательно при
 * запуске» (ставка, ROAS — с `required`, если видны). Креатив (тексты,
 * ссылка, CTA — на каждый креатив отдельно) и поля уровня пачки — в своих
 * блоках. Только ПОЛЯ реестра: опции (`opt.*`) auto-ads в `overrides` не
 * принимает (`unknown-field`) — копии, раскладка и включение идут своими
 * полями пачки.
 */
export function launchEditableDescs(registry, template) {
    const editable = new Set(template.launchEditable);
    return sortDescs(registry.fields.filter((d) => d.enabled))
        .filter((d) => d.group !== 'creative' &&
        !BATCH_LEVEL_KEYS.has(d.key) &&
        d.type !== 'creative_items' &&
        !d.perItem &&
        (d.mode === 'launch' ||
            (d.mode === 'template' && (editable.has(d.key) || isRequiredAtLaunch(d, template)))))
        .map((d) => isRequiredAtLaunch(d, template)
        ? {
            ...d,
            hint: joinHint('Не задано в шаблоне — укажите при запуске', hintWithout(d.hint, LAUNCH_HINT_NOISE)),
            constraints: { ...(d.constraints ?? EMPTY_CONSTRAINTS), required: true },
        }
        : d);
}
/**
 * Фразы подсказки реестра, которые повторяют пояснение экрана: «Обязательна
 * при bid cap / cost cap», «Не задана в шаблоне — вводится при каждом
 * запуске». У поля один короткий текст (v4 D14), а не три подряд.
 */
const LAUNCH_HINT_NOISE = /при каждом запуске|обязательн/i;
export const TEMPLATE_HINT_NOISE = /при каждом запуске/i;
/** Подсказка без фраз, совпавших с `noise`; пусто — null. */
export function hintWithout(hint, noise) {
    if (!hint)
        return null;
    const kept = hint
        .split(/\.\s+|\.$/)
        .map((p) => p.trim())
        .filter((p) => p !== '' && !noise.test(p));
    return kept.length > 0 ? kept.join('. ') : null;
}
/** Фразы подсказки через «. », с точкой в конце; пустые — пропускаются. */
export function joinHint(...parts) {
    return parts
        .filter((p) => p != null && p.trim() !== '')
        .map((p) => p.trim().replace(/\.$/, ''))
        .join('. ')
        .concat('.');
}
const EMPTY_CONSTRAINTS = {
    required: null,
    min: null,
    max: null,
    step: null,
    maxItems: null,
    pattern: null,
    mustContain: null,
};
/**
 * Что задаёт редактор шаблона: поля `mode: template` и — как «значение по
 * умолчанию при запуске» — `mode: launch` (бюджет, старт, копии, раскладка,
 * включение). Без значений на каждый креатив (`perItem`, тексты объявления)
 * и без блоков, у которых нет поля ввода (`creative_items`): auto-ads не
 * принимает их в `values` шаблона.
 */
export function isTemplateEditorDesc(d) {
    if (!d.enabled || d.perItem || d.type === 'creative_items')
        return false;
    return d.mode === 'template' || d.mode === 'launch';
}
/** «Из шаблона» (только чтение на экране запуска): остальные поля шаблона. */
export function templateReadOnlyDescs(registry, template) {
    const editable = new Set(template.launchEditable);
    return allDescs(registry).filter((d) => d.mode === 'template' &&
        !editable.has(d.key) &&
        !BATCH_LEVEL_KEYS.has(d.key) &&
        !isRequiredAtLaunch(d, template));
}
/**
 * Значения шаблона с умолчаниями реестра для отсутствующих ключей
 * (контрактные; для `visibleWhen` и чипов «Из шаблона»).
 */
export function templateValuesWithDefaults(registry, template) {
    const out = {};
    for (const d of allDescs(registry)) {
        if (d.mode === 'fixed')
            out[d.key] = d.fixedValue;
        else if (d.default !== null && d.default !== undefined)
            out[d.key] = d.default;
    }
    if (template)
        Object.assign(out, template.values, template.options);
    return out;
}
/** Реестр + индекс по ключу. */
export function useRegistryIndex() {
    const q = useRegistry();
    const byKey = useMemo(() => {
        const m = new Map();
        if (q.data)
            for (const d of [...q.data.fields, ...q.data.options])
                m.set(d.key, d);
        return m;
    }, [q.data]);
    return { ...q, byKey };
}
/** Все имена слотов аудиторий из профилей кабинетов (объединение, по алфавиту). */
export function slotNamesOf(accounts) {
    const names = new Set();
    for (const a of accounts)
        for (const n of Object.keys(a.profile?.audienceSlots ?? {}))
            names.add(n);
    return [...names].sort((x, y) => x.localeCompare(y, 'ru'));
}
//# sourceMappingURL=use-registry.js.map